/**
 * ElecBid — App Router
 * Google OAuth via Supabase Auth — @browning-electric.ca only
 * Uses React Router for URL-based navigation with persistent layout.
 * Crew access is controlled via the crew_members table (PermissionsProvider).
 */
import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";
import HomeScreen from "./HomeScreen";
import SmartAssessment from "./components/SmartAssessment";
import QuoteBuilder from "./components/QuoteBuilder";
import QuoteDraftReview from "./components/QuoteDraftReview";
import JobberCallback from "./components/JobberCallback";
import LoginScreen from "./components/LoginScreen";
import InvoiceIntelligence from "./components/InvoiceIntelligence";
import ReconciliationPage from "./components/ReconciliationPage";
import CrewPerformance from "./components/CrewPerformance";
import Calculators from "./components/Calculators";
import PriceBook from "./components/PriceBook";
import FeedbackHub from "./components/FeedbackHub";
import ManageCrew from "./components/ManageCrew";
import JobsTab from "./components/JobsTab";
import JobDetail from "./components/JobDetail";
import JobAutoFill from "./components/JobAutoFill";
import QuotesPage from "./components/QuotesPage";
import { TakeoffPage } from "./components/MaterialTakeoff";
import JaiManLearning from "./components/JaiManLearning";
import AdminDiagnostics from "./components/AdminDiagnostics";
import QBAudit from "./components/QBAudit";
import Coach from "./components/Coach";
import CoachingMeetings from "./components/CoachingMeetings";
import TimeAudit from "./components/TimeAudit";
import RndReport from "./components/RndReport";
import OwnerKpis from "./components/OwnerKpis";
import SellRate from "./components/SellRate";
import Marketing from "./components/Marketing";
import CommsBoard from "./components/CommsBoard";
import Hiring from "./components/Hiring";
import FleetTracker from "./components/FleetTracker";
import QuoteTemplates from "./components/QuoteTemplates";
import CompanyDashboard from "./components/CompanyDashboard";
import Bookkeeper from "./components/Bookkeeper";
import Payments from "./components/Payments";
import InvoiceReminders from "./components/InvoiceReminders";
import RaffleAdmin from "./components/RaffleAdmin";
import PipelineHub from "./components/PipelineHub";
import CrewSchedule from "./components/CrewSchedule";
import GeneratorReport from "./components/GeneratorReport";
import PanelScheduleDesigner from "./components/PanelScheduleDesigner";
import SopLibrary from "./components/SopLibrary";
import SocialStudio from "./components/SocialStudio";
import ClientHubPortal from "./components/ClientHubPortal";
import { supabase } from "./lib/supabase";
import { isEmailAllowed } from "./config/appConfig";
import { PermissionsProvider, usePermissions } from "./hooks/usePermissions";

// The generator playbook carries ~350 KB of booklet data: load it only when someone opens the page.
const GeneratorPlaybook = lazy(() => import("./components/GeneratorPlaybook"));
const PermitGuide = lazy(() => import("./components/PermitGuide"));
const PermitsBoard = lazy(() => import("./components/PermitsBoard"));

// Capture the URL hash before Supabase processes it (detects invite/recovery flow)
const INITIAL_HASH = typeof window !== "undefined" ? window.location.hash : "";

// Invite deep link (?invite_token=…&invite_type=invite|recovery) — the emailed
// invite points here instead of Supabase's /auth/v1/verify URL, whose one-time
// token gets burned by mail scanners prefetching links. The token is only
// redeemed when the invitee clicks Accept (AcceptInviteScreen below).
const INITIAL_PARAMS = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
const INITIAL_INVITE_TOKEN = INITIAL_PARAMS?.get("invite_token") || null;
const INITIAL_INVITE_TYPE = INITIAL_PARAMS?.get("invite_type") === "recovery" ? "recovery" : "invite";
// Crew invite (?invite=…) — an app-owned token good for days. It is swapped for
// a fresh Supabase token by /api/accept-invite at the moment of the click, so
// Supabase's one-hour token life no longer decides whether a new hire gets in.
const INITIAL_APP_INVITE = INITIAL_PARAMS?.get("invite") || null;

const LOCAL_PREVIEW_SESSION_KEY = "elecbid.localPreviewSession";

const isLocalPreviewHost = () =>
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

function readLocalPreviewSession() {
  if (!isLocalPreviewHost()) return null;
  try {
    const raw = window.localStorage.getItem(LOCAL_PREVIEW_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = parsed?.user?.email?.toLowerCase?.() || "";
    return isEmailAllowed(email) ? parsed : null;
  } catch {
    return null;
  }
}

function clearLocalPreviewSession() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(LOCAL_PREVIEW_SESSION_KEY);
}

// ── Session context so any component can access auth ──
export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

const SUPABASE_URL = "https://pobobufclzbxpawqtxwx.supabase.co";

function MaintenanceBanner({ onDismiss }) {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "#c9533a", color: "#fff",
      padding: "10px 20px", fontSize: 14,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      fontFamily: "'Roboto', sans-serif", boxShadow: "0 2px 8px rgba(0,0,0,0.4)"
    }}>
      <span>
        Server is temporarily unavailable — some features may not work.{" "}
        <button
          onClick={() => window.location.reload()}
          style={{ color: "#fff", background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit" }}
        >
          Retry
        </button>
      </span>
      <button
        onClick={onDismiss}
        style={{ color: "#fff", background: "none", border: "none", cursor: "pointer", fontSize: 20, lineHeight: 1, paddingLeft: 16 }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// ── Set Password screen (shown after accepting an email invite link) ──────────
function SetPasswordScreen({ onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setError(err.message); setLoading(false); return; }
    onDone();
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#0d0f12",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'IBM Plex Sans',system-ui,sans-serif"
    }}>
      <div style={{
        background: "#161a1f", border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20, padding: "48px 40px", width: "100%", maxWidth: 380,
        textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12
      }}>
        <div style={{ fontSize: 48 }}>⚡</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#f0f0f0", letterSpacing: "-0.5px" }}>ElecBid</div>
        <div style={{ fontSize: 13, color: "#8a919c", marginBottom: 8 }}>Set your password to finish signing up</div>
        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            type="password"
            placeholder="New password (min 8 chars)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            style={{
              width: "100%", padding: "11px 14px", background: "#1e2328",
              border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9,
              color: "#f0f0f0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box"
            }}
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            style={{
              width: "100%", padding: "11px 14px", background: "#1e2328",
              border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9,
              color: "#f0f0f0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box"
            }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 4, padding: "13px 20px", background: loading ? "#1e2328" : "#a5ce3a",
              color: loading ? "#f0f0f0" : "#0d0f12", border: "none", borderRadius: 10,
              fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? "Saving..." : "Set Password & Continue"}
          </button>
        </form>
        {error && (
          <div style={{
            fontSize: 13, color: "#ef4444", background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8,
            padding: "10px 14px", width: "100%"
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Accept Invite screen (invite_token deep link) ─────────────────────────────
// Redeems the Supabase invite/recovery token behind an explicit click so link
// scanners can't consume it. On success the auth listener picks up the new
// session and App routes to SetPasswordScreen.
function AcceptInviteScreen({ token, type, appInvite, onAccepted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canResend, setCanResend] = useState(false);
  const [resent, setResent] = useState(null);

  async function inviteApi(action) {
    const r = await fetch("/api/accept-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: appInvite, ...(action ? { action } : {}) }),
    });
    return { ok: r.ok, data: await r.json().catch(() => ({})) };
  }

  async function handleResend() {
    setLoading(true);
    setError(null);
    try {
      const { ok, data } = await inviteApi("resend");
      if (ok) { setResent(data.sentTo || "your email"); setCanResend(false); }
      else setError(data.code === "throttled"
        ? "A fresh link was already sent a few minutes ago — check your email (and the junk folder)."
        : "Couldn't send a fresh link. Text Dylan and he'll sort it out.");
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
    }
    setLoading(false);
  }

  async function handleAccept() {
    setLoading(true);
    setError(null);
    let verify = { type, token_hash: token };
    if (appInvite) {
      let result;
      try { result = await inviteApi(); } catch { result = { ok: false, data: { code: "network" } }; }
      if (!result.ok) {
        const code = result.data.code;
        if (code === "expired") {
          setCanResend(true);
          setError("This invite link has run out. Tap below and we'll email you a fresh one.");
        } else if (code === "used") {
          setError("This invite was already used. Go to the sign-in page and use the Email tab — tap \"Forgot password?\" there if you need a new one.");
        } else if (code === "invalid") {
          setError("This invite link isn't valid. Ask Dylan to send a fresh one.");
        } else {
          setError("Something went wrong on our end. Try again in a minute.");
        }
        setLoading(false);
        return;
      }
      verify = { type: result.data.type, token_hash: result.data.token_hash };
    }
    const { error: err } = await supabase.auth.verifyOtp(verify);
    if (err) {
      setError(/expired|invalid|not found/i.test(err.message || "")
        ? "This invite link has expired or was already used. Ask Dylan to send a fresh one."
        : err.message);
      setLoading(false);
      return;
    }
    window.history.replaceState(null, "", "/");
    onAccepted();
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#0d0f12",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'IBM Plex Sans',system-ui,sans-serif"
    }}>
      <div style={{
        background: "#161a1f", border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20, padding: "48px 40px", width: "100%", maxWidth: 380,
        textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12
      }}>
        <div style={{ fontSize: 48 }}>⚡</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#f0f0f0", letterSpacing: "-0.5px" }}>ElecBid</div>
        <div style={{ fontSize: 13, color: "#8a919c", marginBottom: 8 }}>
          You've been invited to ElecBid — Browning Electric's field intelligence app.
        </div>
        <button
          onClick={handleAccept}
          disabled={loading}
          style={{
            width: "100%", padding: "13px 20px",
            background: loading ? "#1e2328" : "#a5ce3a",
            color: loading ? "#f0f0f0" : "#0d0f12", border: "none", borderRadius: 10,
            fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "inherit", opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? "Accepting..." : "Accept Invite & Set Password"}
        </button>
        {canResend && (
          <button
            onClick={handleResend}
            disabled={loading}
            style={{
              width: "100%", padding: "13px 20px", background: "#1e2328",
              color: "#f0f0f0", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
              fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: loading ? 0.7 : 1
            }}
          >
            Email me a fresh link
          </button>
        )}
        {resent && (
          <div style={{
            fontSize: 13, color: "#a5ce3a", background: "rgba(165,206,58,0.08)",
            border: "1px solid rgba(165,206,58,0.25)", borderRadius: 8,
            padding: "10px 14px", width: "100%"
          }}>
            Sent a fresh link to {resent}. Open that email and tap the button in it.
          </div>
        )}
        {error && (
          <div style={{
            fontSize: 13, color: "#ef4444", background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8,
            padding: "10px 14px", width: "100%"
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(undefined);
  const [supabaseDown, setSupabaseDown] = useState(false);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [inviteToken, setInviteToken] = useState(INITIAL_INVITE_TOKEN || INITIAL_APP_INVITE);

  // ── Auth listener
  useEffect(() => {
    const previewSession = readLocalPreviewSession();
    if (previewSession) {
      setSession(previewSession);
      return undefined;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        const email = session.user?.email || "";
        if (!isEmailAllowed(email)) {
          supabase.auth.signOut();
          setSession(null);
        } else {
          setSession(session);
        }
      } else {
        setSession(null);
      }
    });

    const isInviteFlow = INITIAL_HASH.includes("type=invite");

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        const email = session.user?.email || "";
        if (!isEmailAllowed(email)) {
          supabase.auth.signOut();
          setSession(null);
        } else {
          setSession(session);
          if (isInviteFlow && _event === "SIGNED_IN") {
            setNeedsPasswordSetup(true);
          }
        }
      } else {
        setSession(null);
        setNeedsPasswordSetup(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Supabase health check on login
  useEffect(() => {
    if (!session) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    fetch(`${SUPABASE_URL}/health`, { signal: controller.signal })
      .then(() => { clearTimeout(timer); /* any HTTP response = server is up */ })
      .catch(() => { clearTimeout(timer); setSupabaseDown(true); });
  }, [!!session]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSignOut() {
    const hadPreviewSession = Boolean(readLocalPreviewSession());
    clearLocalPreviewSession();
    if (hadPreviewSession) {
      setSession(null);
      return;
    }
    await supabase.auth.signOut();
  }

  // ── Client Hub portal (/hub) — a CLIENT-facing surface with its own
  // app-issued auth (api/client-portal.js). Rendered before every crew-session
  // branch (the AcceptInviteScreen precedent): clients must never see the crew
  // LoginScreen, and a crew session in the same browser must not swallow the
  // portal. Deliberately NOT a <Route> — it lives outside the route registry,
  // Layout gate, and PermissionsProvider entirely.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/hub")) {
    return <ClientHubPortal />;
  }

  // ── Loading state
  if (session === undefined) {
    return (
      <div style={{
        minHeight: "100vh", background: "#0d0f12",
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <div style={{
          width: 36, height: 36,
          border: "3px solid rgba(165,206,58,0.2)",
          borderTopColor: "#a5ce3a",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite"
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Invite deep link — redeem the token behind an explicit click
  if (!session && inviteToken) {
    return (
      <AcceptInviteScreen
        token={inviteToken}
        type={INITIAL_INVITE_TYPE}
        appInvite={INITIAL_APP_INVITE}
        onAccepted={() => { setInviteToken(null); setNeedsPasswordSetup(true); }}
      />
    );
  }

  // ── Not logged in
  if (!session) {
    return (
      <Routes>
        <Route path="/jobber/callback" element={<JobberCallback />} />
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );
  }

  // ── Email invite flow — prompt user to set a password before entering the app
  if (needsPasswordSetup) {
    return (
      <SetPasswordScreen onDone={() => {
        // Password is set — spend the invite so the link can't be reused.
        if (INITIAL_APP_INVITE) {
          fetch("/api/accept-invite", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: INITIAL_APP_INVITE, action: "done" }),
          }).catch(() => {});
        }
        setNeedsPasswordSetup(false);
      }} />
    );
  }

  // ── Authenticated routes — wrapped in PermissionsProvider
  // PermissionsProvider checks the crew_members table and shows
  // a loading spinner or access-denied screen as needed.
  return (
    <SessionContext.Provider value={{ session, signOut: handleSignOut }}>
      {supabaseDown && <MaintenanceBanner onDismiss={() => setSupabaseDown(false)} />}
      <PermissionsProvider session={session} signOut={handleSignOut}>
        <Routes>
          {/* Jobber callback — no nav layout */}
          <Route path="/jobber/callback" element={<JobberCallback />} />

          {/* All main app routes share the persistent Layout (bottom nav / sidebar) */}
          <Route element={<Layout />}>
            <Route index element={<HomeOrRedirect />} />
            <Route path="schedule" element={<CrewSchedule />} />
            <Route path="generator-report" element={<GeneratorReport />} />
            <Route path="generator-report/:reportId" element={<GeneratorReport />} />
            <Route path="panel-schedule" element={<PanelScheduleDesigner />} />
            <Route path="panel-schedule/:scheduleId" element={<PanelScheduleDesigner />} />
            <Route path="sops" element={<SopLibrary />} />
            <Route path="sops/:slug" element={<SopLibrary />} />
            <Route path="generator-playbook" element={<Suspense fallback={<div style={{ padding: 24, color: "#8b949e" }}>Loading playbook…</div>}><GeneratorPlaybook /></Suspense>} />
            <Route path="permit-guide" element={<Suspense fallback={<div style={{ padding: 24, color: "#8b949e" }}>Loading permit guide…</div>}><PermitGuide /></Suspense>} />
            <Route path="permits" element={<Suspense fallback={<div style={{ padding: 24, color: "#8b949e" }}>Loading permits…</div>}><PermitsBoard /></Suspense>} />
            <Route path="pipeline" element={<PipelineHub />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="takeoff" element={<TakeoffPage />} />
            <Route path="pricelist" element={<PriceBook />} />
            <Route path="feedback" element={<FeedbackHub />} />
            <Route path="calculators" element={<Calculators />} />
            <Route path="crew" element={<CrewPerformance />} />
            <Route path="jobs" element={<JobsTab />} />
            <Route path="job/:jobNum" element={<JobDetail />} />
            <Route path="expenses" element={<InvoiceIntelligence />} />
            <Route path="bookkeeper" element={<Bookkeeper />} />
            <Route path="payments" element={<Payments />} />
            <Route path="invoice-reminders" element={<InvoiceReminders />} />
            {/* /raffle itself is the PUBLIC ticket form (vercel.json rewrite →
                api/raffle.js), so the admin ledger sits one level down. */}
            <Route path="raffle/admin" element={<RaffleAdmin />} />
            <Route path="qb-audit" element={<QBAudit />} />
            <Route path="coach" element={<Coach />} />
            <Route path="coach/meetings" element={<CoachingMeetings />} />
            <Route path="coach/day-audit" element={<TimeAudit />} />
            <Route path="coach/rnd" element={<RndReport />} />
            <Route path="coach/owner-kpis" element={<OwnerKpis />} />
            <Route path="coach/sell-rate" element={<SellRate />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="comms" element={<CommsBoard />} />
            <Route path="social" element={<SocialStudio />} />
            <Route path="hiring" element={<Hiring />} />
            <Route path="fleet" element={<FleetTracker />} />
            {/* Consolidated into the single Quotes tab; deep-link :id kept */}
            <Route path="quote-templates" element={<Navigate to="/quotes?view=templates" replace />} />
            <Route path="quote-templates/:id" element={<QuoteTemplates />} />
            <Route path="reconciliation" element={<ReconciliationPage />} />
            <Route path="manage-crew" element={<ManageCrew />} />
            {/* /profitability (JobProfitability.jsx) removed 2026-08-30. It had been
                dead behind a commented-out route since it was written, and never ran
                against real data: it read jobber_timesheets.date (the column is
                start_at, so its From/To filters silently matched everything) and asked
                PostgREST for 5k/20k/10k rows against a hard 1000-row cap, so every
                margin came off ~10% of the timesheets. Per-job margin already lives on
                /jobs; the per-tech GP% board is CrewPerformance → Profit Bonuses.
                Recover from git if it's ever rebuilt (paginate + start_at + gate on
                view_jobs, NOT view_profitability — see SESSION_LOG 2026-08-30). */}
            <Route path="autofill" element={<JobAutoFill />} />
            {/* Folded into the Quotes tab → Backend Library sub-view (redirects preserve old links) */}
            <Route path="quote-db" element={<Navigate to="/quotes?view=backends" replace />} />
            <Route path="backend-builder" element={<Navigate to="/quotes?view=backends&sub=author" replace />} />
            <Route path="jaiman-learning" element={<JaiManLearning />} />
            <Route path="admin" element={<AdminDiagnostics />} />
            <Route path="companies" element={<CompanyDashboard />} />
            <Route path="assess/smart/:id" element={<SmartAssessment />} />
            <Route path="quote/:id" element={<QuoteDraftReview />} />
            <Route path="quote/legacy/:id" element={<QuoteBuilder />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PermissionsProvider>
    </SessionContext.Provider>
  );
}

// ── Per-company landing ──────────────────────────────────────────────────────
// Browning (and any company with the Requests/home pipeline enabled) lands on
// HomeScreen. A company without it is sent to their first enabled feature, so a
// second company never lands on another company's Jobber pipeline.
function HomeOrRedirect() {
  const { featureOn } = usePermissions() || {};
  if (typeof featureOn !== "function" || featureOn("requests")) return <HomeScreen />;
  const order = [
    ["expenses", "/expenses"], ["pricelist", "/pricelist"], ["jobs", "/jobs"],
    ["crew", "/crew"], ["calculators", "/calculators"], ["quotes", "/quotes"],
    ["reconciliation", "/reconciliation"], ["marketing", "/marketing"],
    ["coach", "/coach"], ["feedback", "/feedback"],
  ];
  const first = order.find(([k]) => featureOn(k));
  return <Navigate to={first ? first[1] : "/feedback"} replace />;
}

